import type { ReactElement } from "react";
import { createBrowserRouter, Navigate, RouterProvider } from "react-router";
import { Casca } from "./componentes/layout";
import { RotaProtegida } from "./componentes/rota";
import { Landing } from "./paginas/Landing";
import { Entrar } from "./paginas/Entrar";
import { Convite } from "./paginas/Convite";
import { Recuperar } from "./paginas/Recuperar";
import { Redefinir } from "./paginas/Redefinir";
import { PainelDoCliente } from "./paginas/PainelDoCliente";
import { FormulariosDaImplantacao } from "./paginas/FormulariosDaImplantacao";
import { RespostaDaTrilha } from "./paginas/RespostaDaTrilha";
import { CatalogoDeRecursos } from "./paginas/CatalogoDeRecursos";
import { CatalogoDoKickoff } from "./paginas/CatalogoDoKickoff";
import { ConsoleAdmin } from "./paginas/ConsoleAdmin";
import { InicioDoConsole } from "./paginas/InicioDoConsole";
import { ListaDeImplantacoes } from "./paginas/ListaDeImplantacoes";
import { NovaImplantacao } from "./paginas/NovaImplantacao";
import { ImplantacaoNoConsole } from "./paginas/ImplantacaoNoConsole";
import { RevisaoDoKickoff } from "./paginas/RevisaoDoKickoff";
import { PacoteDoKickoff } from "./paginas/PacoteDoKickoff";
import { CofreDaImplantacao } from "./paginas/CofreDaImplantacao";
import { ReunioesDaImplantacao } from "./paginas/ReunioesDaImplantacao";
import { ReuniaoNoConsole } from "./paginas/ReuniaoNoConsole";
import { ReunioesDoCliente } from "./paginas/ReunioesDoCliente";
import { UsuariosDaConta } from "./paginas/UsuariosDaConta";
import { ModuloDaImplantacao } from "./paginas/ModuloDaImplantacao";
import { ContratoDoCliente } from "./paginas/ContratoDoCliente";
import { FinanceiroDoCliente } from "./paginas/FinanceiroDoCliente";
import { GeoeduDoCliente } from "./paginas/GeoeduDoCliente";
import { MateriaisDoCliente } from "./paginas/MateriaisDoCliente";
import { Chamado, ListaDeChamados } from "./paginas/Chamados";
import { ArtigoDaCentral, CentralDeAjuda } from "./paginas/CentralDeAjuda";
import { BaseDeConhecimento } from "./paginas/BaseDeConhecimento";
import { EdicaoDeArtigo, NovoArtigo } from "./paginas/EditorDeArtigo";
import { Relatorios } from "./paginas/Relatorios";
import { Automacao } from "./paginas/Automacao";
import { Canais } from "./paginas/Canais";
import { AbrirChamadoPublico } from "./paginas/AbrirChamadoPublico";
import { ConfiguracaoDosChamados } from "./paginas/ConfiguracaoDosChamados";
import { HorasEAprovacoes } from "./paginas/HorasEAprovacoes";
import { Ativos } from "./paginas/Ativos";
import { Cadastros } from "./paginas/Cadastros";
import { DetalheDaOrganizacao } from "./paginas/DetalheDaOrganizacao";
import { AgendaDeCompromissos } from "./paginas/AgendaDeCompromissos";
import { MuralDeAvisos } from "./paginas/MuralDeAvisos";
import { MuralDeFeedback } from "./paginas/MuralDeFeedback";
import { SaudeDaOrganizacao } from "./paginas/SaudeDaOrganizacao";
import { SaudeDosClientes } from "./paginas/SaudeDosClientes";
import { Pesquisas } from "./paginas/Pesquisas";
import { ResponderPesquisa } from "./paginas/ResponderPesquisa";
import { MigracaoMovidesk } from "./paginas/MigracaoMovidesk";
import { PaineisDaEquipe } from "./paginas/PaineisDaEquipe";
import { VerPainel } from "./paginas/VerPainel";
import { PerfisDeAcesso } from "./paginas/PerfisDeAcesso";
import { UsuariosInternos } from "./paginas/UsuariosInternos";
import { SegurancaDaConta } from "./paginas/SegurancaDaConta";
import { WebChat } from "./paginas/WebChat";
import { Exportacoes } from "./paginas/Exportacoes";
import { ImpressaoDoChamado } from "./paginas/ImpressaoDoChamado";
import { AmostraDoDesignSystem } from "./componentes/AmostraDoDesignSystem";
import {
  ErroInesperado,
  ErroPublico,
  NaoEncontrada,
  NaoEncontradaNaArea,
} from "./paginas/NaoEncontrada";

import type { CaminhoComTrilha } from "./nucleo/trilha";

const AREA_DO_CLIENTE = "Portal de Implantação";
const AREA_INTERNA = "Console";

/* Três grupos de rota, e a diferença entre eles é de segurança, não de layout:
     · público      — landing e telas de acesso; qualquer pessoa na internet abre;
     · /app         — área do cliente; exige sessão;
     · /admin       — backoffice da GEOvendas; exige sessão E lado interno.
   A aplicação do cliente não mora mais em `/`: a raiz é a fachada pública, e é ela que
   qualquer visitante encontra. Quem tem sessão entra por `/app`. */
/**
 * Declara uma rota com sessão pelo caminho ABSOLUTO, e não pelo trecho relativo.
 *
 * O tipo `CaminhoComTrilha` só aceita caminho que tenha título registrado em
 * `nucleo/trilha`. Rota nova sem título **não compila** — é o que faz a trilha nascer
 * junto com a tela em vez de depender de alguém lembrar de registrá-la depois.
 */
function rota(
  base: "/app" | "/admin",
  caminho: CaminhoComTrilha,
  element: ReactElement,
) {
  const relativo = caminho.slice(base.length).replace(/^\//, "");
  return relativo ? { path: relativo, element } : { index: true as const, element };
}

const roteador = createBrowserRouter([
  { path: "/", element: <Landing />, errorElement: <ErroPublico /> },
  { path: "/entrar", element: <Entrar />, errorElement: <ErroPublico /> },
  { path: "/convite/:token", element: <Convite />, errorElement: <ErroPublico /> },
  { path: "/recuperar", element: <Recuperar />, errorElement: <ErroPublico /> },
  { path: "/redefinir/:token", element: <Redefinir />, errorElement: <ErroPublico /> },
  /* O formulário público de abertura de chamado: quem abre o link não tem conta. O token do link
     autoriza UMA empresa e é tudo o que a API precisa para receber o pedido. */
  { path: "/abrir/:token", element: <AbrirChamadoPublico />, errorElement: <ErroPublico /> },
  /* A pesquisa de satisfação pelo link do e-mail: pública de propósito. O token do endereço é a
     autorização (uso único) e a página só mostra a pergunta — nada do chamado nem da empresa. */
  { path: "/pesquisa/:token", element: <ResponderPesquisa />, errorElement: <ErroPublico /> },
  {
    path: "/app",
    element: <RotaProtegida area={AREA_DO_CLIENTE} lado="cliente" />,
    errorElement: (
      <Casca area={AREA_DO_CLIENTE} lado="cliente">
        <ErroInesperado />
      </Casca>
    ),
    children: [
      rota("/app", "/app", <PainelDoCliente />),
      rota("/app", "/app/formularios", <FormulariosDaImplantacao />),
      rota("/app", "/app/formularios/:trilha", <RespostaDaTrilha />),
      /* As atas do cliente. Endereço próprio, e não um bloco do painel, porque uma ata é um
         documento ao qual se volta — o cliente reabre daqui a três meses para conferir o que foi
         combinado — e porque o painel responde uma pergunta só: de quem é a vez e o que vem
         depois. O painel aponta para cá. */
      rota("/app", "/app/reunioes", <ReunioesDoCliente />),
      rota("/app", "/app/modulos/:modulo", <ModuloDaImplantacao />),
      rota("/app", "/app/materiais", <MateriaisDoCliente />),
      rota("/app", "/app/usuarios", <UsuariosDaConta />),
      rota("/app", "/app/ativos", <Ativos />),
      rota("/app", "/app/seguranca", <SegurancaDaConta />),
      rota("/app", "/app/chamados", <ListaDeChamados />),
      rota("/app", "/app/chamados/:id", <Chamado />),
      rota("/app", "/app/chamados/:id/imprimir", <ImpressaoDoChamado />),
      /* A Central de ajuda: a base de conhecimento como o cliente a lê. O servidor decide o que
         cada pessoa vê (publicado e público, ou da empresa dela); a tela mostra o que vier. */
      rota("/app", "/app/ajuda", <CentralDeAjuda />),
      rota("/app", "/app/ajuda/:id", <ArtigoDaCentral />),
      /* A tela Contrato (item 10 da Amanda): datas, escopo, dados do cadastro, o PDF assinado e os
         termos de encerramento. Permissão própria (`contrato:portal`), conferida na tela e no servidor. */
      rota("/app", "/app/contrato", <ContratoDoCliente />),
      rota("/app", "/app/geoedu", <GeoeduDoCliente />),
      rota("/app", "/app/financeiro", <FinanceiroDoCliente />),
      { path: "*", element: <NaoEncontradaNaArea voltarPara="/app" /> },
    ],
  },
  {
    path: "/admin",
    element: <RotaProtegida area={AREA_INTERNA} lado="interna" exigeInterno />,
    errorElement: (
      <Casca area={AREA_INTERNA} lado="interna">
        <ErroInesperado />
      </Casca>
    ),
    children: [
      /* A tela inicial: o painel que o papel recebe e, sem ele, o console de sempre. */
      rota("/admin", "/admin", <InicioDoConsole />),
      rota("/admin", "/admin/visao-geral", <ConsoleAdmin />),
      rota("/admin", "/admin/implantacoes", <ListaDeImplantacoes />),
      /* O cadastro do catálogo. Fica fora de `/admin/implantacoes` de propósito: quem
         cadastra uma implantação ESCOLHE do catálogo; quem administra o catálogo muda o
         que todo mundo pode escolher. São coisas diferentes, e a permissão também é. */
      rota("/admin", "/admin/catalogo", <CatalogoDeRecursos />),
      /* O catálogo do KICKOFF — as perguntas que o cliente responde. Endereço próprio, e
         não uma aba do catálogo de Recursos Plus, porque são dois catálogos com públicos
         diferentes: um é o que o comercial vende, o outro é o que a consultoria pergunta. */
      rota("/admin", "/admin/catalogo-do-kickoff", <CatalogoDoKickoff />),
      rota("/admin", "/admin/chamados", <ListaDeChamados interno />),
      rota("/admin", "/admin/chamados/:id", <Chamado interno />),
      rota("/admin", "/admin/chamados/:id/imprimir", <ImpressaoDoChamado interno />),
      /* Os arquivos que a própria pessoa pediu (fila e relatórios), gerados em segundo plano. */
      rota("/admin", "/admin/exportacoes", <Exportacoes />),
      /* O console da base de conhecimento: rascunho, revisão, publicação e o histórico de
         versões. `novo` é literal e por isso ganha de `:id`. */
      rota("/admin", "/admin/conhecimento", <BaseDeConhecimento />),
      rota("/admin", "/admin/conhecimento/novo", <NovoArtigo />),
      rota("/admin", "/admin/conhecimento/:id", <EdicaoDeArtigo />),
      rota("/admin", "/admin/relatorios", <Relatorios />),
      rota("/admin", "/admin/automacao", <Automacao />),
      rota("/admin", "/admin/canais", <Canais />),
      rota("/admin", "/admin/configuracao-de-chamados", <ConfiguracaoDosChamados />),
      rota("/admin", "/admin/horas", <HorasEAprovacoes />),
      /* Cadastros da equipe: organizações e pessoas (dados, contatos, endereços, importação CSV,
         fusão de duplicados), ativos, mural de avisos e pesquisas de satisfação. */
      rota("/admin", "/admin/cadastros", <Cadastros />),
      rota("/admin", "/admin/cadastros/organizacoes/:id", <DetalheDaOrganizacao />),
      rota("/admin", "/admin/ativos", <Ativos interno />),
      rota("/admin", "/admin/avisos", <MuralDeAvisos />),
      rota("/admin", "/admin/feedback", <MuralDeFeedback />),
      rota("/admin", "/admin/pesquisas", <Pesquisas />),
      /* Sucesso do cliente: carteira de saúde, a saúde de uma organização, e a agenda interna. */
      rota("/admin", "/admin/sucesso", <SaudeDosClientes />),
      rota("/admin", "/admin/sucesso/:organizacaoId", <SaudeDaOrganizacao />),
      rota("/admin", "/admin/compromissos", <AgendaDeCompromissos />),
      rota("/admin", "/admin/migracao", <MigracaoMovidesk />),
      /* Painéis configuráveis: a administração monta; `:id` é a visualização de um painel. */
      rota("/admin", "/admin/paineis", <PaineisDaEquipe />),
      rota("/admin", "/admin/paineis/:id", <VerPainel />),
      /* Perfis de acesso (só a administração) e a segurança da conta de quem está logado. */
      rota("/admin", "/admin/usuarios-internos", <UsuariosInternos />),
      rota("/admin", "/admin/perfis", <PerfisDeAcesso />),
      rota("/admin", "/admin/seguranca", <SegurancaDaConta />),
      /* O atendimento por chat: fila em tempo real, a conversa aberta e (para a administração) a
         configuração de cada empresa. */
      rota("/admin", "/admin/webchat", <WebChat />),
      rota("/admin", "/admin/implantacoes/nova", <NovaImplantacao />),
      /* A tela de uma implantação — o que faltava para o consultor trabalhar. A carteira dava
         uma linha por cliente e as duas telas de detalhe (revisão e cofre) viviam soltas: a
         esteira de um cliente, a linha do tempo e a liberação do ambiente não tinham tela
         nenhuma e só saíam por `curl`. Esta rota é o lugar onde elas se encontram. */
      rota("/admin", "/admin/implantacoes/:id", <ImplantacaoNoConsole />),
      /* A revisão do kickoff é o outro lado da tela 4: a contradição entre trilhas é
         detectada desde a F5 e a spec §2.3 manda APRESENTÁ-LA a quem revisa. Sem esta rota
         ela saía só por `curl`. */
      rota("/admin", "/admin/implantacoes/:id/kickoff", <RevisaoDoKickoff />),
      /* O cofre da tela 8. A rota é por implantação, e não por segredo, porque é assim que o
         console navega — e porque a fronteira entre clientes aqui é a política de linha do
         banco, não o id da URL: credencial de outro cliente não aparece, e a resposta é a mesma
         de um id que não existe. */
      rota("/admin", "/admin/implantacoes/:id/cofre", <CofreDaImplantacao />),
      /* A agenda da tela 6. Rota por implantação porque a pergunta que traz alguém aqui é "o que
         já conversamos com este cliente" — e porque as pendências das atas atravessam reuniões: a
         que saiu do kickoff é cobrada na de acompanhamento. */
      rota("/admin", "/admin/implantacoes/:id/reunioes", <ReunioesDaImplantacao />),
      /* A tela 7, de UMA reunião: o material, a ata e a fronteira do que sai daqui. Endereço
         próprio porque a ata é reaberta muito depois da conversa — e porque `ata_item` é a
         fronteira mais estreita do portal, que merece uma tela inteira em vez de um painel
         espremido dentro de outra. */
      rota("/admin", "/admin/implantacoes/:id/reunioes/:reuniao", <ReuniaoNoConsole />),
      /* O pacote da tela 11. Rota própria, e não uma aba da implantação, porque o que se faz
         aqui é um ato com efeito fora do portal — gerar já tenta criar a issue `Kick Off` no
         ONB — e porque a descrição consolidada precisa de um endereço para o consultor voltar
         nela. Versão antiga continua legível: retrato que não se reabre não serve de prova. */
      rota("/admin", "/admin/implantacoes/:id/pacote", <PacoteDoKickoff />),
      /* A amostra do design system saiu da área do cliente e vive atrás do muro interno: é
         referência de quem constrói o portal, não conteúdo de quem contratou o produto. */
      rota("/admin", "/admin/design", <AmostraDoDesignSystem />),
      /* `/admin/console` é o endereço que a spec §5.3 e o ADR 0004 escreveram como destino do
         handoff (`next=/admin/console`) antes de o console existir de fato — e o console mora em
         `/admin`. `destino_seguro`, no servidor, aceita o caminho (é do próprio portal), então o
         `303` chegava aqui e caía no `*` desta área: o consultor terminava o SSO dentro de uma
         tela de "não encontrada". Enquanto a outra metade não for escrita, o portal honra os dois
         endereços em vez de depender de os dois repositórios combinarem a mesma letra. */
      { path: "console", element: <Navigate to="/admin" replace /> },
      { path: "*", element: <NaoEncontradaNaArea voltarPara="/admin" /> },
    ],
  },
  { path: "*", element: <NaoEncontrada /> },
]);

export function App() {
  return <RouterProvider router={roteador} />;
}
